/**
 * `MatchSummary` (what `/api/me/matches` actually sends) → the row the scorepad
 * draws.
 *
 * The client used to declare its own `RecentMatch` — `{id, note, us, them}` —
 * against a server sending `{matchId, gameId, finalScores, …}`. Only `won`
 * overlapped, so every row in "Recent matches" and the whole history screen
 * rendered a Won/Lost chip and three blanks, and React logged a stream of
 * `key={undefined}` (VERDICT H4). The shape now comes from
 * `shared/src/protocol/rest.ts`, which both sides are meant to read, and this
 * file is the only place the wire shape is turned into words and numbers.
 *
 * Kept free of React so it can be tested as what it is: a mapping.
 */
import type { GameId, MatchSummary, TeamId } from '@cards/shared'

/** The player's record for one game — each lobby tab keeps its own. */
export function matchesPath(game: GameId): string {
  return `/api/me/matches?game=${encodeURIComponent(game)}`
}

export interface MatchRow {
  readonly id: string
  /** null when the match never reached a result — abandoned, or still open. */
  readonly won: boolean | null
  /** Who else was at the table, as a scorepad would write it. */
  readonly note: string
  /** Scores ordered for the reader when that can be worked out, and by team
   *  index when it cannot. */
  readonly us: number
  readonly them: number
}

/**
 * Which team the reader was on.
 *
 * `MatchSummary.team` now carries this outright: the server knows which row of
 * `match_players` is the reader's, and says so. Everything below it is the
 * older inference, kept for the one case the field cannot cover — a match with
 * no result, where the server still knows the seat but a reader looking at an
 * abandoned row gets `team` from the same source anyway.
 *
 * The inference is derived from "did *they* win" and "who won", so it needs a
 * finished two-team match to say anything. That was the whole reason the note
 * below had to match people by display name; it no longer does.
 */
export function viewerTeam(summary: MatchSummary): TeamId | null {
  if (summary.team !== null) return summary.team
  if (summary.won === null || summary.winnerTeam === null) return null
  if (summary.finalScores.length !== 2) return null
  return summary.won ? summary.winnerTeam : summary.winnerTeam === 0 ? 1 : 0
}

const COUNT_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six']

/** `Dev, Cara and Bob` — an Oxford-less list, the way it is said aloud. */
function listOf(names: readonly string[]): string {
  if (names.length === 0) return ''
  if (names.length === 1) return names[0]
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

const botPhraseOf = (bots: number): string =>
  bots === 0 ? '' : `${COUNT_WORDS[bots] ?? bots} ${bots === 1 ? 'bot' : 'bots'}`

/**
 * Who else was at the table, as a scorepad would write it.
 *
 * With the reader's own seat on the summary this can say what a scorepad
 * actually says — who you played *with* and who you played *against* — rather
 * than one undifferentiated list of three names. The partnership is the thing
 * a player remembers a game by, and it was the one fact the row could not
 * state.
 *
 * `viewerName` remains the fallback for a summary with no seat (a reader who
 * was not at the table), and it is only ever a fallback: matching on a display
 * name is wrong whenever someone renames themselves or two people share a
 * name.
 */
export function playerNote(summary: MatchSummary, viewerName: string): string {
  const seat = summary.seat
  if (seat !== null) {
    const byTeam = summary.team ?? summary.players.find((p) => p.seat === seat)?.team ?? null
    const partners: string[] = []
    const opponents: string[] = []
    let partnerBots = 0
    let opponentBots = 0
    for (const player of summary.players) {
      if (player.seat === seat) continue
      const mine = byTeam !== null && player.team === byTeam
      if (player.isBot) {
        if (mine) partnerBots++
        else opponentBots++
        continue
      }
      ;(mine ? partners : opponents).push(player.name)
    }
    const withPart = [listOf(partners), botPhraseOf(partnerBots)].filter(Boolean).join(', ')
    const againstPart = [listOf(opponents), botPhraseOf(opponentBots)].filter(Boolean).join(', ')
    // Partnerships are the normal case but not the only possible one: a
    // three-hand game or a solo contract leaves one side empty, and the note
    // should still read as English.
    if (withPart && againstPart) return `with ${withPart}, against ${againstPart}`
    if (againstPart) return `against ${againstPart}`
    if (withPart) return `with ${withPart}`
    return ''
  }

  let skipped = false
  const humans: string[] = []
  let bots = 0
  for (const player of summary.players) {
    if (player.isBot) {
      bots++
      continue
    }
    // Drop the reader's own name once. If the name does not match — they
    // renamed themselves since, or two people share a name — the row simply
    // lists everyone, which reads as slightly odd rather than as wrong.
    if (!skipped && player.name.trim().toLowerCase() === viewerName.trim().toLowerCase()) {
      skipped = true
      continue
    }
    humans.push(player.name)
  }
  return [listOf(humans), botPhraseOf(bots)].filter(Boolean).join(', ')
}

export function toMatchRow(summary: MatchSummary, viewerName: string): MatchRow {
  const team = viewerTeam(summary)
  const scores = summary.finalScores
  const us = (team === null ? scores[0] : scores[team]) ?? 0
  const them = (team === null ? scores[1] : scores[team === 0 ? 1 : 0]) ?? 0
  return {
    id: summary.matchId,
    won: summary.won,
    note: playerNote(summary, viewerName),
    us,
    them,
  }
}

export function toMatchRows(
  matches: readonly MatchSummary[],
  viewerName: string,
): readonly MatchRow[] {
  return matches.map((m) => toMatchRow(m, viewerName))
}

/** The word in the chip. A match that was abandoned has no winner and saying
 *  "Lost" about it would be a small lie the scorepad should not tell. */
export function resultWord(won: boolean | null): string {
  return won === null ? 'Unfinished' : won ? 'Won' : 'Lost'
}

export function resultClass(won: boolean | null): string {
  return won === null ? 'u' : won ? 'w' : 'l'
}
